import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  House,
  Search,
  User,
  AudioLines,
  MonitorCog,
  LogIn,
  LogOut,
  Settings,
  Plus,
  Users,
  Music,
  Heart,
  TrendingUp,
  Calendar,
  MessageCircle,
  Bookmark,
  Upload,
  Shield,
  Banknote,
  BarChart3,
  Wallet,
  History,
  Trophy,
  Headphones,
  HelpCircle,
  Info,
  UserCheck,
  Bell,
  CreditCard,
} from "lucide-react";
import { GiftCardIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { cn } from "@/lib/utils";
import { useBottomNavigation } from "@/hooks/useBottomNavigation";
import { hapticFeedback } from "@/utils/mobileInteractions";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { useUserVerification } from "@/components/profile/hooks/useUserVerification";
import { useHideOnScroll } from "@/hooks/useHideOnScroll";
import { MobileProfileSheet } from "./navbar/MobileProfileSheet";
import { useOverlayContext } from "@/contexts/OverlayContext";

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  requiredAuth?: boolean;
  roles?: string[];
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  requiredAuth?: boolean;
  roles?: string[];
}

export function BottomNavigation() {
  const { user, userRole, isActiveRoute, shouldShowBottomNav } =
    useBottomNavigation();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fanProfileSheetOpen, setFanProfileSheetOpen] = useState(false);
  const { isVerified } = useUserVerification(user);
  const scrollHidden = useHideOnScroll();
  const { isOverlayOpen } = useOverlayContext();
  const isHidden =
    scrollHidden &&
    !drawerOpen &&
    !isProfileSheetOpen &&
    !fanProfileSheetOpen &&
    !isOverlayOpen;
  const isFan = !!user && userRole === "fan";
  const getDashboardLink = () => `/dashboard/${userRole || "fan"}`;

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsProfileSheetOpen(false);
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  const handleUploadClick = () => {
    hapticFeedback.light();
    setDrawerOpen(false);
    if (userRole === "artist") {
      // Navigate to artist dashboard tracks tab
      navigate("/dashboard/artist?tab=tracks");
    }
  };

  const handleSponsorsClick = () => {
    hapticFeedback.light();
    navigate("/sponsors");
  };

  const handleAboutClick = () => {
    hapticFeedback.light();
    navigate("/about");
  };

  const handleNavClick = (path: string) => {
    hapticFeedback.light();
    setDrawerOpen(false);
    navigate(path);
  };

  // Role-based navigation items for the drawer
  const getExpandedNavItems = () => {
    if (!user) {
      // Guest users - prioritize engagement and discovery
      return [
        {
          name: "Top Charts",
          path: "/charts",
          icon: <TrendingUp size={20} />,
          description: "Trending music",
        },
        {
          name: "Discover Artists",
          path: "/profiles",
          icon: <Search size={20} />,
          description: "Browse profiles",
        },
        {
          name: "Competitions",
          path: "/competitions",
          icon: <Trophy size={20} />,
          description: "Music contests",
        },
        {
          name: "Community",
          path: "/community",
          icon: <Users size={20} />,
          description: "Join discussions",
        },
        {
          name: "For Sponsors",
          path: "/sponsors",
          icon: <Banknote size={20} />,
          description: "Support artists",
        },
        {
          name: "For Artists",
          path: "/artists",
          icon: <Music size={20} />,
          description: "Start earning",
        },
        {
          name: "Login",
          path: "/login",
          icon: <LogIn size={20} />,
          description: "Sign in",
        },
        {
          name: "Register",
          path: "/register",
          icon: <UserCheck size={20} />,
          description: "Join free",
        },
      ];
    }

    switch (userRole) {
      case "artist":
        return [
          {
            name: "Notifications",
            path: "/dashboard/artist?tab=notifications",
            icon: <Bell size={20} />,
            description: "View notifications",
          },
          {
            name: "Upload Track",
            path: "/dashboard/artist?tab=tracks",
            icon: <Upload size={20} />,
            description: "Upload new music",
          },
          {
            name: "Get Verified",
            path: "/verification",
            icon: <Shield size={20} />,
            description: "Artist verification",
          },
          ...(isVerified
            ? [
                {
                  name: "Analytics",
                  path: "/dashboard/artist?tab=analytics",
                  icon: <BarChart3 size={20} />,
                  description: "Performance metrics",
                },
              ]
            : []),
          {
            name: "Earnings",
            path: "/dashboard/artist?tab=earnings",
            icon: <Banknote size={20} />,
            description: "Financial dashboard",
          },
          {
            name: "Gifts",
            path: "/dashboard/artist?tab=gifts",
            icon: <HugeiconsIcon icon={GiftCardIcon} size={20} />,
            description: "Received gifts",
          },
          {
            name: "Sponsors",
            path: "/dashboard/artist?tab=sponsorships",
            icon: <Users size={20} />,
            description: "Sponsor relationships",
          },
          {
            name: "Support",
            path: "/support",
            icon: <MessageCircle size={20} />,
            description: "Technical support",
          },
        ];

      case "sponsor":
        return [
          {
            name: "Notifications",
            path: "/dashboard/sponsor?tab=notifications",
            icon: <Bell size={20} />,
            description: "View notifications",
          },
          {
            name: "Find Artists",
            path: "/profiles",
            icon: <Search size={20} />,
            description: "Discover artists",
          },
          {
            name: "Wallet",
            path: "/dashboard/sponsor?tab=wallet",
            icon: <Wallet size={20} />,
            description: "Account balance",
          },
          {
            name: "Sponsored Tracks",
            path: "/dashboard/sponsor?tab=tracks",
            icon: <Music size={20} />,
            description: "Managed sponsorships",
          },
          ...(isVerified
            ? [
                {
                  name: "Analytics",
                  path: "/dashboard/sponsor?tab=analytics",
                  icon: <BarChart3 size={20} />,
                  description: "ROI metrics",
                },
              ]
            : []),
          {
            name: "Get Verified",
            path: "/verification",
            icon: <Shield size={20} />,
            description: "Sponsor verification",
          },
          {
            name: "Gifting",
            path: "/dashboard/sponsor?tab=gifting",
            icon: <HugeiconsIcon icon={GiftCardIcon} size={20} />,
            description: "Send gifts to artists",
          },
          {
            name: "Support",
            path: "/support",
            icon: <MessageCircle size={20} />,
            description: "Business support",
          },
        ];

      case "fan":
        return [
          {
            name: "Notifications",
            path: "/dashboard/fan?tab=notifications",
            icon: <Bell size={20} />,
            description: "View notifications",
          },
          {
            name: "Send Gifts",
            path: "/dashboard/fan?tab=gifting",
            icon: <HugeiconsIcon icon={GiftCardIcon} size={20} />,
            description: "Gift favorite artists",
          },
          {
            name: "Streaming History",
            path: "/dashboard/fan?tab=history",
            icon: <History size={20} />,
            description: "Music activity",
          },
          {
            name: "Support",
            path: "/support",
            icon: <MessageCircle size={20} />,
            description: "User support",
          },
        ];

      default:
        return [
          {
            name: "Notifications",
            path: "/dashboard/artist?tab=notifications",
            icon: <Bell size={20} />,
            description: "View notifications",
          },
          {
            name: "Discover",
            path: "/profiles",
            icon: <Search size={20} />,
            description: "Browse content",
          },
          {
            name: "Community",
            path: "/community",
            icon: <Users size={20} />,
            description: "Join discussions",
          },
          {
            name: "Support",
            path: "/support",
            icon: <MessageCircle size={20} />,
            description: "Get help",
          },
        ];
    }
  };

  const expandedNavItems = getExpandedNavItems();

  // Navigation items for different user states
  const guestNavItems: NavItem[] = [
    { name: "Home", path: "/", icon: <House size={20} /> },
    { name: "Plans", path: "/plans", icon: <CreditCard size={20} /> },
    { name: "Discover", path: "/profiles", icon: <Search size={20} /> },
    { name: "Login", path: "/login", icon: <LogIn size={20} /> },
  ];

  const authenticatedNavItems: NavItem[] = [
    { name: "Home", path: "/", icon: <House size={20} /> },
    {
      name: "Dashboard",
      path: `/dashboard/${userRole}`,
      icon: <MonitorCog size={20} />,
      requiredAuth: true,
    },
    { name: "Plans", path: "/plans", icon: <CreditCard size={20} /> },
    { name: "Discover", path: "/profiles", icon: <Search size={20} /> },
  ];

  const navItems = user ? authenticatedNavItems : guestNavItems;

  // Don't render bottom navigation on certain pages
  if (!shouldShowBottomNav) {
    return null;
  }

  return (
    <nav
      data-bottom-nav
      className={cn(
        "md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md border-t border-border/10 safe-area-pb transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none",
        isHidden ? "translate-y-full" : "translate-y-0",
      )}
      aria-hidden={isHidden}
    >
      <div className="safe-area-pb">
        <div className="flex items-center justify-around px-2 py-3 relative">
          {navItems.slice(0, 2).map((item) => {
            const isActive = isActiveRoute(item.path);

            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => hapticFeedback.light()}
                className={cn(
                  "flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 rounded-xl bottom-nav-transition active:scale-95 touch-manipulation",
                  isActive
                    ? "text-primary-light"
                    : "text-muted-foreground hover:text-foreground active:bg-accent/50",
                )}
              >
                <div
                  className={cn(
                    "mb-1 transition-all duration-200",
                    isActive && "scale-110",
                  )}
                >
                  {item.icon}
                </div>
                <span
                  className={cn(
                    "text-xs font-medium truncate leading-tight",
                    isActive
                      ? "text-primary-light font-semibold"
                      : "text-muted-foreground",
                  )}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}

          {/* Center Button - Fans: Profile Menu, Others: Quick Access Drawer */}
          {isFan ? (
            <>
              <button
                onClick={() => {
                  hapticFeedback.light();
                  setFanProfileSheetOpen(true);
                }}
                className="flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 touch-manipulation z-50"
                aria-label="Open profile menu"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center active:scale-95 transition-all duration-200 border border-primary/20">
                  <Plus size={24} className="text-foreground" />
                </div>
              </button>
              <MobileProfileSheet
                user={user}
                userRole={userRole}
                onSignOut={handleSignOut}
                getDashboardLink={getDashboardLink}
                open={fanProfileSheetOpen}
                onOpenChange={setFanProfileSheetOpen}
                hideTrigger
              />
            </>
          ) : (
            <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
              <DrawerTrigger asChild>
                <button
                  onClick={() => hapticFeedback.light()}
                  className="flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 touch-manipulation z-50"
                >
                <div className="w-12 h-12 rounded-full flex items-center justify-center active:scale-95 transition-all duration-200 border border-primary/20">
                    <Plus size={24} className="text-foreground" />
                  </div>
                </button>
              </DrawerTrigger>
              <DrawerContent className=" border-t border-border/10 backdrop-blur-md">
                <div className="mx-auto w-full max-w-sm p-6 pb-24 max-h-[80vh] overflow-y-auto">
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Quick Access
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate to your favorite features
                    </p>
                  </div>

                  {/* Role-specific CTA buttons - Above grid for visibility */}
                  {user && (
                    <div className="mb-4">
                      {userRole === "artist" && (
                      <button
                          onClick={handleUploadClick}
                          className="w-full flex items-center justify-center p-3 rounded-xl text-foreground font-medium transition-all duration-200 active:scale-95 touch-manipulation"
                        >
                          <Upload size={20} className="mr-2" />
                          Upload Track
                        </button>
                      )}
                      {userRole === "sponsor" && (
                        <button
                          onClick={() =>
                            handleNavClick("/dashboard/sponsor?tab=wallet")
                          }
                          className="w-full flex items-center justify-center p-3 rounded-xl text-foreground font-medium transition-all duration-200 active:scale-95 touch-manipulation"
                        >
                          <Wallet size={20} className="mr-2" />
                          Top Up Wallet
                        </button>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {expandedNavItems.map((item) => (
                      <button
                        key={item.name}
                        onClick={() => handleNavClick(item.path)}
                        className="flex flex-col items-center justify-start p-4 pt-5 min-h-[110px] rounded-xl bg-muted/50 hover:bg-muted active:bg-muted/80 border border-border/50 transition-all duration-200 active:scale-95 touch-manipulation"
                      >
                        <div className="mb-2 text-primary-light flex-shrink-0">
                          {item.icon}
                        </div>
                        <span className="text-sm font-medium text-foreground text-center leading-tight flex-shrink-0">
                          {item.name}
                        </span>
                        <span className="text-xs text-muted-foreground text-center mt-1 line-clamp-2">
                          {item.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </DrawerContent>
            </Drawer>
          )}

          {navItems.slice(2).map((item) => {
            const isActive = isActiveRoute(item.path);

            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => hapticFeedback.light()}
                className={cn(
                  "flex flex-col items-center justify-center min-w-0 flex-1 px-2 py-2 rounded-xl bottom-nav-transition active:scale-95 touch-manipulation",
                  isActive
                    ? "text-primary-light"
                    : "text-muted-foreground hover:text-foreground active:bg-accent/50",
                )}
              >
                <div
                  className={cn(
                    "mb-1 transition-all duration-200",
                    isActive && "scale-110",
                  )}
                >
                  {item.icon}
                </div>
                <span
                  className={cn(
                    "text-xs font-medium truncate leading-tight",
                    isActive
                      ? "text-primary-light font-semibold"
                      : "text-muted-foreground",
                  )}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
